import {Injectable, Inject, Optional, PLATFORM_ID} from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { REQUEST } from '../providers/http-context-providers';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly storageKey = 'lang';
  private language: string;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject(REQUEST) private request: any,
  ) {
    this.language = this.detectLanguage();
  }

  private detectLanguage(): string {
    let host: string | undefined;
    let langParam: string | null = null;
    let lang = 'en-US';

    const cookieLang = this.getCookie('lang');
    const storedLang = this.getStoredLanguage();

    if (isPlatformServer(this.platformId)) {
      const request = this.getRequest();
      if (request) {
        host = this.getRequestHost();
        langParam = this.getQueryParam(request.url, 'lang');
      }
    } else if (isPlatformBrowser(this.platformId)) {
      host = window.location.hostname;
      langParam = new URLSearchParams(window.location.search).get('lang');
    }

    if (langParam) {
      lang =  this.getLanguageFromParam(langParam);
      this.setCookie('lang', lang);
      return lang;
    }

    if (isPlatformBrowser(this.platformId) && storedLang) {
      return storedLang;
    }

    if (isPlatformServer(this.platformId) && cookieLang) {
      return cookieLang;
    }

    if (host) {
      const hostLang = this.getLanguageFromSubdomain(host);
      if (hostLang) {
        return hostLang;
      }
    }

    const langFromAcceptHeaders = this.getLanguageFromAcceptLanguage();

    if (langFromAcceptHeaders) {
      return langFromAcceptHeaders;
    }

    return lang;
  }

  private getLanguageFromSubdomain(hostname: string): string | null {
    const parts = hostname.split('.');
    if (parts.length <= 2) {
      return null;
    }

    const subdomain = parts[0]?.toLowerCase();

    if (!subdomain || !['pl-pl', 'de-de', 'en-us'].includes(subdomain)) {
      return null;
    }

    return this.getMappedLanguage(subdomain);
  }

  public getLanguage(): string {
    return this.language;
  }

  public setLanguage(lang: string, persist: boolean = true) {
    lang = this.getMappedLanguage(lang.toLowerCase());
    this.language = lang;
    this.setCookie('lang', lang);
    if (persist) {
      this.setStoredLanguage(lang);
    }
  }

  private getRequest() {
    // return isPlatformServer(this.platformId) ? require('express').request : null;
    return this.request ? this.request : null;
  }

  private getLanguageFromParam(param: string): string {
    return this.getMappedLanguage(param.toLowerCase());
  }

  private getQueryParam(url: string, paramName: string): string | null {
    const urlObj = new URL(url, this.getRequestOrigin());
    return urlObj.searchParams.get(paramName);
  }

  private getRequestOrigin(): string {
    const protocol = this.getRequestProtocol();
    const host = this.getRequestHost() || this.getRequest()?.headers?.host || 'localhost';

    return `${protocol}://${host}`;
  }

  private getRequestHost(): string | undefined {
    const request = this.getRequest();
    if (!request?.headers) {
      return undefined;
    }

    return this.readHeaderValue(request.headers['x-forwarded-host'])
      || this.readHeaderValue(request.headers['x-original-host'])
      || this.getHostFromForwardedHeader(this.readHeaderValue(request.headers['forwarded']))
      || this.readHeaderValue(request.headers.host);
  }

  private getRequestProtocol(): string {
    const request = this.getRequest();
    if (!request?.headers) {
      return 'http';
    }

    return this.readHeaderValue(request.headers['x-forwarded-proto'])
      || this.getProtoFromForwardedHeader(this.readHeaderValue(request.headers['forwarded']))
      || request.protocol
      || 'http';
  }

  private getHostFromForwardedHeader(forwardedHeader: string | undefined): string | undefined {
    if (!forwardedHeader) {
      return undefined;
    }

    const firstEntry = forwardedHeader.split(',')[0]?.trim();
    const hostMatch = firstEntry?.match(/host=([^;]+)/i);

    return hostMatch?.[1]?.replace(/^"|"$/g, '') || undefined;
  }

  private getProtoFromForwardedHeader(forwardedHeader: string | undefined): string | undefined {
    if (!forwardedHeader) {
      return undefined;
    }

    const firstEntry = forwardedHeader.split(',')[0]?.trim();
    const protoMatch = firstEntry?.match(/proto=([^;]+)/i);

    return protoMatch?.[1]?.replace(/^"|"$/g, '') || undefined;
  }

  private readHeaderValue(header: string | string[] | undefined): string | undefined {
    if (Array.isArray(header)) {
      return header[0];
    }

    if (typeof header !== 'string') {
      return undefined;
    }

    return header.split(',')[0]?.trim() || undefined;
  }

  private getMappedLanguage(param: string): string {
    switch (true) {
      case !!['pl', 'pl-pl', 'pl_pl'].find(e => e === param): return 'pl-PL';
      case !!['en', 'en-us', 'en-gb', 'en_us'].find(e => e === param): return 'en-US';
      case !!['de', 'de-de', 'de_de'].find(e => e === param): return 'de-DE';
      default:  return 'en-US';
    }
  }

  private getCookie(name: string): string | null {
    if (isPlatformServer(this.platformId)) {
      const request = this.getRequest();
      if (request) {
        const cookies = request.headers?.cookie;
        if (cookies) {
          const match = cookies.match(new RegExp('(^| )' + name + '=([^;]+)'));
          if (match) {
            return match[2];
          }
        }
      }
    } else if (isPlatformBrowser(this.platformId)) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) {
        return parts.pop()?.split(';').shift() || null;
      }
    }
    return null;
  }

  private setCookie(name: string, value: string, days: number = 7) {
    if (isPlatformServer(this.platformId)) {
      const request = this.getRequest();
      if (request) {
        const response = request.res;
        const expires = new Date();
        expires.setDate(expires.getDate() + days);
        response.setHeader('Set-Cookie', `${name}=${value}; Path=/; Expires=${expires.toUTCString()}`);
      }
    } else if (isPlatformBrowser(this.platformId)) {
      const expires = new Date();
      expires.setDate(expires.getDate() + days);
      document.cookie = `${name}=${value}; path=/; expires=${expires.toUTCString()}`;
    }
  }

  private getLanguageFromAcceptLanguage(): string | null {
    let languageFromHeader: string | undefined;

    if (isPlatformServer(this.platformId)) {
      const request = this.getRequest();
      languageFromHeader = request?.headers['accept-language'];
    } else if (isPlatformBrowser(this.platformId)) {
      languageFromHeader = navigator.language || navigator.languages?.[0];
    }

    if (languageFromHeader) {
      const lang = this.getPrimaryLanguageFromAcceptLanguage(languageFromHeader);
      return this.getMappedLanguage(lang.toLowerCase());
    }

    return null;
  }

  private getPrimaryLanguageFromAcceptLanguage(acceptLanguageHeader: string): string {
    const languages = acceptLanguageHeader.split(',').map(lang => lang.split(';')[0].trim());
    return languages.length > 0 ? languages[0] : 'en-US';
  }

  private getStoredLanguage(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    try {
      const value = localStorage.getItem(this.storageKey) || '';
      if (!value) {
        return null;
      }
      return this.getMappedLanguage(value.toLowerCase());
    } catch (error) {
      return null;
    }
  }

  private setStoredLanguage(language: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      localStorage.setItem(this.storageKey, language);
    } catch (error) {
      // ignore storage write errors
    }
  }
}
